import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Download, Filter, Eye, EyeOff } from 'lucide-react';
import { generateRevenueGrowthData, formatCurrency, exportChart } from '../utils/visualization';
import type { FilterOptions, ExportConfig } from '../types/visualization';

interface RevenueGrowthDashboardProps {
  filters?: FilterOptions;
  onFilterChange?: (filters: FilterOptions) => void;
  className?: string;
}

export const RevenueGrowthDashboard: React.FC<RevenueGrowthDashboardProps> = ({
  filters = {},
  onFilterChange,
  className = '',
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCompanies, setVisibleCompanies] = useState<Set<string>>(new Set(['thumbtack', 'angi', 'taskrabbit', 'porch_group']));
  const [viewMode, setViewMode] = useState<'absolute' | 'growth'>('absolute');

  const rawData = useMemo(() => generateRevenueGrowthData(filters), [filters]);
  
  // Transform data for line chart format
  const chartData = useMemo(() => {
    const groupedByDate = rawData.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = { date: item.date } as Record<string, any>;
      }
      (acc[item.date] as Record<string, any>)[item.companyId] = item.value;
      return acc;
    }, {} as Record<string, Record<string, any>>);

    return Object.values(groupedByDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [rawData]);

  // Calculate growth rates
  const growthData = useMemo(() => {
    if (viewMode === 'absolute') return chartData;
    
    return chartData.map((item, index) => {
      if (index === 0) return { ...item };
      
      const result: Record<string, any> = { date: item.date };
      Object.keys(item).forEach(key => {
        if (key !== 'date' && chartData[index - 1]?.[key]) {
          const currentValue = (item as any)[key];
          const previousValue = (chartData[index - 1] as any)[key];
          result[key] = ((currentValue - previousValue) / previousValue) * 100;
        }
      });
      return result;
    }).slice(1); // Remove first item as it has no growth data
  }, [chartData, viewMode]);

  const displayData = viewMode === 'growth' ? growthData : chartData;

  const companies = [
    { id: 'thumbtack', name: 'Thumbtack', color: '#1a73e8' },
    { id: 'taskrabbit', name: 'TaskRabbit', color: '#00d084' },
    { id: 'angi', name: 'Angi', color: '#ff6900' },
    { id: 'handy', name: 'Handy', color: '#6b46c1' },
    { id: 'porch_group', name: 'Porch Group', color: '#ef4444' },
  ];

  const toggleCompanyVisibility = (companyId: string) => {
    const newVisibleCompanies = new Set(visibleCompanies);
    if (newVisibleCompanies.has(companyId)) {
      newVisibleCompanies.delete(companyId);
    } else {
      newVisibleCompanies.add(companyId);
    }
    setVisibleCompanies(newVisibleCompanies);
  };

  const handleExport = async (format: 'png' | 'csv') => {
    const chartElement = document.getElementById('revenue-growth-chart');
    if (!chartElement) return;

    const config: ExportConfig = {
      format,
      filename: 'revenue-growth-dashboard',
      includeData: true,
    };

    await exportChart(config, chartElement, rawData);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <h3 className="font-semibold text-gray-900 mb-2">{label}</h3>
          <div className="space-y-1">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-gray-700">
                    {companies.find(c => c.id === entry.dataKey)?.name}
                  </span>
                </div>
                <span className="text-sm font-medium">
                  {viewMode === 'growth' 
                    ? `${entry.value?.toFixed(1)}%`
                    : formatCurrency(entry.value)
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate summary statistics
  const currentYearData = chartData[chartData.length - 1];
  const previousYearData = chartData[chartData.length - 2];
  const summaryStats = companies.map(company => {
    const current = currentYearData?.[company.id] || 0;
    const previous = previousYearData?.[company.id] || 0;
    const growth = previous ? ((current - previous) / previous) * 100 : 0;
    
    return {
      ...company,
      current,
      growth,
      isPositive: growth >= 0,
    };
  });

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Revenue Growth Dashboard</h2>
          <p className="text-sm text-gray-600 mt-1">
            Multi-company revenue trends 2020-2025 (projected)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setViewMode('absolute')}
              className={`px-3 py-1 text-sm rounded ${
                viewMode === 'absolute'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Revenue
            </button>
            <button
              onClick={() => setViewMode('growth')}
              className={`px-3 py-1 text-sm rounded ${
                viewMode === 'growth'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Growth %
            </button>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleExport('png')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as PNG"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as CSV"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        {summaryStats.map((company) => (
          <div key={company.id} className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">{company.name}</span>
              <div className="flex items-center gap-1">
                {company.isPositive ? (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                )}
                <span className={`text-sm font-medium ${
                  company.isPositive ? 'text-green-600' : 'text-red-600'
                }`}>
                  {company.growth.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {formatCurrency(company.current)}
            </div>
          </div>
        ))}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <select
                value={filters.company || ''}
                onChange={(e) => onFilterChange?.({ ...filters, company: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Companies</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year Range</label>
              <select
                value={filters.year || ''}
                onChange={(e) => onFilterChange?.({ ...filters, year: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">2020-2025</option>
                <option value="2024">2024 Only</option>
                <option value="2023">2023 Only</option>
                <option value="2022">2022 Only</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Company Visibility Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-sm font-medium text-gray-700">Show/Hide:</span>
        {companies.map((company) => (
          <button
            key={company.id}
            onClick={() => toggleCompanyVisibility(company.id)}
            className={`flex items-center gap-2 px-3 py-1 text-sm rounded-md border transition-colors ${
              visibleCompanies.has(company.id)
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-gray-50 border-gray-200 text-gray-500'
            }`}
          >
            {visibleCompanies.has(company.id) ? (
              <Eye className="h-3 w-3" />
            ) : (
              <EyeOff className="h-3 w-3" />
            )}
            {company.name}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div id="revenue-growth-chart" className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={displayData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis 
              tickFormatter={(value) => 
                viewMode === 'growth' ? `${value.toFixed(0)}%` : formatCurrency(value)
              }
              label={{ 
                value: viewMode === 'growth' ? 'Growth Rate (%)' : 'Revenue (Millions USD)', 
                angle: -90, 
                position: 'insideLeft' 
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {companies
              .filter(company => visibleCompanies.has(company.id))
              .map((company) => (
              <Line
                key={company.id}
                type="monotone"
                dataKey={company.id}
                stroke={company.color}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                name={company.name}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};